import type { ActivityMessage, Message } from '@ag-ui/client'
import {
  MCP_APPS_ACTIVITY_TYPE,
  TURN_METADATA_ACTIVITY_TYPE,
} from '../config/chat.config'
import type { MCPAppsActivityContent } from '../types/mcp'
import { isRecord } from './json'

type AssistantMessage = Extract<Message, { role: 'assistant' }>
type MCPAppsActivityMessage = ActivityMessage & {
  content: MCPAppsActivityContent
}

export interface TurnMetadataActivityContent {
  source: string
  followUpSuggestions: string[]
}

type TurnMetadataActivityMessage = ActivityMessage & {
  content: TurnMetadataActivityContent
}

export interface DisplayMessageItem {
  id: string
  kind: 'message'
  message: Message
}

export interface DisplayAssistantTurn {
  id: string
  kind: 'assistant-turn'
  userMessageId: string | null
  message: AssistantMessage
  mcpApps: MCPAppsActivityMessage[]
  hasToolCalls: boolean
  hasFinalResponse: boolean
  hasRenderableContent: boolean
  source?: string
  followUpSuggestions: string[]
}

export type ChatDisplayItem = DisplayMessageItem | DisplayAssistantTurn

interface AssistantTurnBuilder {
  id: string
  userMessageId: string | null
  textParts: string[]
  mcpApps: MCPAppsActivityMessage[]
  hasToolCalls: boolean
  source?: string
  followUpSuggestions: string[]
}

function isStandaloneMessage(message: Message): boolean {
  return message.role === 'system' || message.role === 'developer'
}

export function asMCPAppsContent(
  content: unknown,
): MCPAppsActivityContent | null {
  if (!isRecord(content) || typeof content.resourceUri !== 'string') {
    return null
  }

  return {
    result: content.result,
    resourceUri: content.resourceUri,
    serverHash:
      typeof content.serverHash === 'string' ? content.serverHash : undefined,
    serverId:
      typeof content.serverId === 'string' ? content.serverId : undefined,
    toolInput: isRecord(content.toolInput) ? content.toolInput : undefined,
  }
}

export function isMCPAppsActivity(
  message: Message,
): message is ActivityMessage & { content: MCPAppsActivityContent } {
  return (
    message.role === 'activity' &&
    message.activityType === MCP_APPS_ACTIVITY_TYPE &&
    asMCPAppsContent(message.content) !== null
  )
}

// The widgets (`ui://widget/<name>.html`) that go without the assistant avatar on mobile. The
// support-actions card draws its own icon next to its text; the form cards are boxes that use
// the full width instead of sitting in the column beside an avatar.
const SUPPORT_ACTIONS_WIDGET = 'support-actions'
const NO_AVATAR_ON_MOBILE_WIDGETS = [
  SUPPORT_ACTIONS_WIDGET,
  'create-incident-card',
  'request-callback-card',
  'live-agent-chat-card',
  'teams-action-card',
]

/**
 * The support-actions card the AI server appends after an answer (`after_answer`), as opposed to
 * one that is the reply itself. The avatar stays beside the answer text then, so on mobile the
 * card is widened to the full row to give the forms it opens the full width.
 */
export function isSupportActionsAfterAnswer(content: MCPAppsActivityContent): boolean {
  return (
    content.resourceUri.includes(SUPPORT_ACTIONS_WIDGET) &&
    content.toolInput?.after_answer === true
  )
}

/** Whether a reply made only of these widgets should hide the assistant avatar on mobile. */
export function hidesAvatarOnMobile(
  mcpApps: readonly { content: MCPAppsActivityContent }[],
): boolean {
  return mcpApps.some((app) =>
    NO_AVATAR_ON_MOBILE_WIDGETS.some((name) => app.content.resourceUri.includes(name)),
  )
}

function asTurnMetadataContent(
  content: unknown,
): TurnMetadataActivityContent | null {
  if (!isRecord(content) || typeof content.source !== 'string') {
    return null
  }

  return {
    source: content.source,
    followUpSuggestions: Array.isArray(content.followUpSuggestions)
      ? content.followUpSuggestions.filter(
          (suggestion): suggestion is string => typeof suggestion === 'string',
        )
      : [],
  }
}

export function isTurnMetadataActivity(
  message: Message,
): message is TurnMetadataActivityMessage {
  return (
    message.role === 'activity' &&
    message.activityType === TURN_METADATA_ACTIVITY_TYPE &&
    asTurnMetadataContent(message.content) !== null
  )
}

export function displayMessageContent(content: Message['content']): string {
  if (typeof content === 'string') return content
  if (!Array.isArray(content)) return ''

  return content
    .map((part) => {
      if (part.type === 'text') return part.text
      return `[${part.type} input]`
    })
    .join('\n')
}

export function findLastUserMessageId(
  messages: ReadonlyArray<Readonly<Message>>,
): string | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === 'user') return messages[index].id
  }

  return null
}

/**
 * True for the text-less tool-call message the AI server attaches after an answer
 * to show the support options (`support-actions` with `after_answer`). It follows
 * the answer rather than preceding it, so it carries no preamble to discard.
 */
function isAppendedAfterAnswer(message: AssistantMessage): boolean {
  if (displayMessageContent(message.content).trim().length > 0) return false

  return (message.toolCalls ?? []).every((toolCall) => {
    if (toolCall.function.name !== 'support-actions') return false

    try {
      const args: unknown = JSON.parse(toolCall.function.arguments)
      return isRecord(args) && args.after_answer === true
    } catch {
      return false
    }
  })
}

function assistantTurnId(anchorMessageId: string): string {
  return `assistant-turn:${anchorMessageId}`
}

function createAssistantTurn(
  anchorMessageId: string,
  userMessageId: string | null,
): AssistantTurnBuilder {
  return {
    id: assistantTurnId(anchorMessageId),
    userMessageId,
    textParts: [],
    mcpApps: [],
    hasToolCalls: false,
    followUpSuggestions: [],
  }
}

/**
 * Builds a user-facing transcript without changing AG-UI's protocol messages.
 * Tool calls, tool results, activities and the final assistant response stay
 * separate in `agent.messages`, but are represented by one assistant turn in
 * the UI. A user message is the stable boundary (and React key) for that turn.
 */
export function buildChatDisplayItems(
  messages: readonly Message[],
  activeUserMessageId: string | null = null,
): ChatDisplayItem[] {
  const items: ChatDisplayItem[] = []
  let currentTurn: AssistantTurnBuilder | null = null

  const flushAssistantTurn = () => {
    if (!currentTurn) return

    const content = currentTurn.textParts.join('\n\n')
    const hasFinalResponse = content.trim().length > 0
    const hasRenderableContent =
      hasFinalResponse || currentTurn.mcpApps.length > 0
    const isActiveTurn =
      activeUserMessageId !== null &&
      currentTurn.userMessageId === activeUserMessageId

    if (hasRenderableContent || isActiveTurn) {
      items.push({
        id: currentTurn.id,
        kind: 'assistant-turn',
        userMessageId: currentTurn.userMessageId,
        message: {
          id: currentTurn.id,
          role: 'assistant',
          content,
        },
        mcpApps: currentTurn.mcpApps,
        hasToolCalls: currentTurn.hasToolCalls,
        hasFinalResponse,
        hasRenderableContent,
        source: currentTurn.source,
        followUpSuggestions: currentTurn.followUpSuggestions,
      })
    }

    currentTurn = null
  }

  for (const message of messages) {
    if (message.role === 'user') {
      flushAssistantTurn()
      items.push({ id: message.id, kind: 'message', message })
      currentTurn = createAssistantTurn(message.id, message.id)
      continue
    }

    if (isStandaloneMessage(message) && !currentTurn) {
      items.push({ id: message.id, kind: 'message', message })
      continue
    }

    if (message.role === 'assistant') {
      currentTurn ??= createAssistantTurn(message.id, null)
      const content = displayMessageContent(message.content)
      const isToolCallMessage = (message.toolCalls?.length ?? 0) > 0
      if (isToolCallMessage) {
        currentTurn.hasToolCalls = true
        // Text emitted before a tool call is progress/preamble, not the final
        // answer. Keep it in AG-UI history, but do not surface it as a result.
        if (!isAppendedAfterAnswer(message)) {
          currentTurn.textParts = []
        }
      }
      if (!isToolCallMessage && content.trim().length > 0) {
        currentTurn.textParts.push(content)
      }
      continue
    }

    if (isMCPAppsActivity(message)) {
      currentTurn ??= createAssistantTurn(message.id, null)
      currentTurn.mcpApps.push(message)
      continue
    }

    if (isTurnMetadataActivity(message)) {
      currentTurn ??= createAssistantTurn(message.id, null)
      currentTurn.source = message.content.source
      currentTurn.followUpSuggestions = message.content.followUpSuggestions
      continue
    }

    // Tool results, reasoning, custom activities and run-internal messages are
    // deliberately kept in the AG-UI history but hidden inside the UI turn.
  }

  flushAssistantTurn()
  return items
}
